import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Feb 2026 · iOS PWA handoff bridge. The `open.html` bridge page writes a
// { path, ts, source } blob to `localStorage.am_pending_deeplink` before
// letting the user tap "Open in App". Since Safari on iOS CANNOT
// programmatically jump into an installed home-screen PWA, the practical
// UX is: user taps email → sees bridge → the target is saved → user
// manually opens the PWA from their home screen → this component picks
// up the pending target and navigates there automatically.
//
// Only runs once per mount. Bails if:
//   • no pending target
//   • target is stale (older than TTL)
//   • current pathname already matches target (no self-loop)
//   • user isn't authed AND target is a protected route (LoginPage will
//     still see `am_pending_deeplink` and consume it after login via
//     `?next=` semantics — but simplest form: we just clear the entry
//     when user hits /login and let the login flow use ?next=)
//
// Consumes-then-clears so we never fire twice.
const STORAGE_KEY = 'am_pending_deeplink';
const TTL_MS = 15 * 60 * 1000;

// Routes a logged-out user can visit without an auth guard bouncing them.
const PUBLIC_ROUTES = new Set([
  '/', '/login', '/register', '/forgot-password', '/reset-password', '/contact',
]);

function readPending() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.path !== 'string' || typeof parsed.ts !== 'number') return null;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    if (!parsed.path.startsWith('/')) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function clearPending() {
  try { window.localStorage.removeItem(STORAGE_KEY); } catch (_) { /* ignore */ }
}

export function PendingDeepLinkResolver() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const consumedRef = useRef(false);

  useEffect(() => {
    // Wait for auth check to finish so we know whether user is logged in.
    if (loading) return;
    if (consumedRef.current) return;

    const pending = readPending();
    if (!pending) return;

    // Never redirect if we're already on the target.
    if (location.pathname === pending.path || location.pathname + location.search === pending.path) {
      clearPending();
      consumedRef.current = true;
      return;
    }

    // If the target is a protected route AND the user isn't authed, let
    // the LoginPage's `?next=` mechanism handle it. Rewrite our stored
    // path onto `/login?next=…` so the login handoff completes the trip.
    const isPublic = PUBLIC_ROUTES.has(pending.path.split('?')[0]);
    if (!user && !isPublic) {
      consumedRef.current = true;
      clearPending();
      const encoded = encodeURIComponent(pending.path);
      navigate(`/login?next=${encoded}`, { replace: true });
      return;
    }

    // Authenticated (or public target) — consume and jump.
    consumedRef.current = true;
    clearPending();
    navigate(pending.path, { replace: true });
  }, [user, loading, location.pathname, location.search, navigate]);

  return null;
}

export default PendingDeepLinkResolver;
